"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "food-storage-calculator",
  "refrigerator-organization-planner",
  "refrigerator-size-calculator",
  "energy-cost-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Food Waste Calculator",
  category: "Cost & Savings" as const,
  mediumDescription:
    "Estimate how much money your household throws away on uneaten food each month and year. Built on USDA Economic Research Service data: US households waste roughly 30% of food purchased, costing the average family $1,500+ per year.",
  faqs: [
    { question: "How much food does the average US household waste?", answer: "USDA Economic Research Service estimates 30–40% of the US food supply is wasted. Households account for the largest share — averaging roughly $1,500+ in wasted food per family of four per year, per USDA and EPA estimates." },
    { question: "What's the biggest source of household food waste?", answer: "Fresh produce and prepared leftovers, by weight. Items spoil before they're eaten — usually because they're stored at the wrong temperature, in the wrong location, or simply forgotten in the back of the fridge." },
    { question: "Does fridge organization actually reduce waste?", answer: "Yes. Households that practice FIFO (first-in, first-out) and store food per USDA zone guidance report significantly less spoilage. A clear-front view of leftovers and produce makes the difference between using food and forgetting it." },
    { question: "How does shopping frequency affect waste?", answer: "Bulk monthly shoppers waste more per dollar than weekly shoppers, because more food sits longer before being eaten. Weekly meal-planning paired with weekly shopping is the lowest-waste pattern." },
    { question: "Should I freeze food to reduce waste?", answer: "Yes. Most fresh items — meat, bread, herbs, ripe fruit, even dairy products like cheese and butter — freeze well and extend shelf life by months. A separate freezer or large freezer compartment pays back quickly in waste reduction." },
    { question: "What's the environmental impact?", answer: "EPA estimates US food waste accounts for 8% of national greenhouse gas emissions when methane from landfilled food is counted. Reducing household waste is one of the highest-leverage personal climate actions." },
  ],
  relatedTools: ["food-storage-calculator", "refrigerator-organization-planner", "refrigerator-size-calculator"],
}

export function FoodWasteCalculatorPageClient() {
  const [householdSize, setHouseholdSize] = useState("3")
  const [grocerySpending, setGrocerySpending] = useState("800")
  const [wasteFrequency, setWasteFrequency] = useState("sometimes")

  // Original calc logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const size = readCount(householdSize, { min: 1, max: 20 }) ?? 0
    const spending = parseFloat(grocerySpending)
    if (!size || !spending || spending <= 0) return null

    const wasteMultiplier =
      wasteFrequency === "rarely" ? 0.15 :
      wasteFrequency === "sometimes" ? 0.25 :
      wasteFrequency === "often" ? 0.35 :
      wasteFrequency === "frequently" ? 0.45 : 0.25

    const weeklyWaste = (spending / 4) * wasteMultiplier
    const monthlyWaste = spending * wasteMultiplier
    const yearlyWaste = monthlyWaste * 12
    const poundsWasted = yearlyWaste * 0.5 // ~$1 of groceries ≈ 0.5 lb of food
    const percentageWasted = wasteMultiplier * 100

    return {
      weeklyWaste: Math.round(weeklyWaste),
      monthlyWaste: Math.round(monthlyWaste),
      yearlyWaste: Math.round(yearlyWaste),
      poundsWasted: Math.round(poundsWasted),
      percentageWasted: Math.round(percentageWasted),
    }
  }, [householdSize, grocerySpending, wasteFrequency])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What you actually"
      heroTitleItalic="throw"
      heroTitlePost="away."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "USDA ERS / EPA waste estimates"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "30–40%", label: "US food wasted", note: "USDA ERS" },
            { value: "$1,500+", label: "Avg family/year", note: "USDA" },
            { value: "8%", label: "Of US emissions", note: "EPA" },
            { value: "0.5 lb", label: "Per dollar", note: "Avg" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "food-waste-calculator")}
      cta={{
        title: "Cut waste with a better fridge layout.",
        body: "Browse French door fridges with adjustable shelves, full-extension drawers, and LED interior lighting — the layouts that make leftovers and produce easy to see and easy to use.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Household, spending,
                <br />
                honest answer.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Be honest about waste — most US households underestimate by a lot.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Household size
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={householdSize}
                    onChange={(e) => setHouseholdSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Monthly grocery spending ($)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={grocerySpending}
                    onChange={(e) => setGrocerySpending(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    How often do you toss food?
                  </label>
                  <select
                    value={wasteFrequency}
                    onChange={(e) => setWasteFrequency(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="rarely">Rarely (~15%)</option>
                    <option value="sometimes">Sometimes (~25%)</option>
                    <option value="often">Often (~35%)</option>
                    <option value="frequently">Frequently (~45%)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> US household average is ~30% (USDA ERS).
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Wasted per year
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(48px, 7vw, 84px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.yearlyWaste?.toLocaleString() ?? 0}`}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    Roughly {result?.poundsWasted?.toLocaleString() ?? 0} lbs of food per year — or {result?.percentageWasted ?? 0}% of grocery spend.
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Per week
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.weeklyWaste ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Per month
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.monthlyWaste ?? "—"}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Layouts that cut waste"
          title="French door fridges to compare"
          description="French doors put fresh food at eye level, so leftovers stay in view."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Numbers from <em className="italic text-[#ff9951]">USDA</em> + EPA.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Waste percentages calibrated to USDA Economic Research Service estimates. Pound-per-dollar conversion uses the ERS average retail food cost.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Waste percentage",
                  d: "Rarely 15%, sometimes 25% (US average), often 35%, frequently 45%. Calibrated to USDA ERS national waste estimates.",
                },
                {
                  n: "02",
                  t: "Dollar conversion",
                  d: "Monthly spend × waste rate = monthly $. ×12 for yearly cost. ÷4 for weekly cost.",
                },
                {
                  n: "03",
                  t: "Pounds wasted",
                  d: "Yearly $ × 0.5 lb/$. Approximate weight conversion using USDA ERS average retail food costs.",
                },
                {
                  n: "04",
                  t: "Why it varies by household",
                  d: "Larger households waste a smaller percentage per person (more eaters, faster turnover). Bulk shoppers waste more per dollar than weekly shoppers.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges to compare"
          description="Larger fresh-food capacity at eye level — easier to see what you have before it spoils."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
